import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Box, TableCell, TableSortLabel, Tooltip, useMediaQuery, Theme } from '@mui/material';
import { Order, OrderTableType } from 'models/order';
import { OrderItemContext, OrderItemEvent } from 'models/order-item-events';
import { OrderBy } from 'models/search-result';
import { ListViewProfile } from 'models/organization';
import { setOrderListColumnWidthsTemplate } from 'apollo/templates/user/mutation.graphql';
import { panNumberFeatureStatusTemplate } from 'apollo/templates/pan-number';
import { currentUserVar } from 'apollo/reactive-variables/user';
import {
    ORDER_LIST_COLUMN_DEFINITIONS,
    ORDER_LIST_COLUMN_MAX_WIDTH,
    ORDER_LIST_COLUMN_MIN_WIDTH,
    ORDER_LIST_FRAMEWORK_CELL_WIDTHS,
    OrderListColumnKey,
    SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE,
    getActiveOrderListColumnKeys,
    isSystemDefaultProfile,
} from './column-registry';
import OrderListRow from './order-list-row.component';
import { ResizableProfileTable } from 'components/shared/resizable-profile-table';

type OrderListTableProps = {
    tableType: OrderTableType;
    orders: Order[];
    onOrderItemEvent: (event: OrderItemEvent) => void;
    onContextMenu: (orderContext: OrderItemContext) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    profile?: ListViewProfile;
    setOrderOnField?: (field: string) => void;
    orderBy?: OrderBy;
    isAtLimit?: boolean;
    checkedOrderIds?: Set<string>;
};

const OrderListTable: FC<OrderListTableProps> = ({
    tableType,
    orders,
    onOrderItemEvent,
    onContextMenu,
    batchActionSelected,
    onHandleCheckedOrders,
    profile,
    setOrderOnField,
    orderBy,
    isAtLimit,
    checkedOrderIds,
}: OrderListTableProps) => {
    const { t: translate } = useTranslation(['orders', 'common', 'workflow', 'transport']);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const currentUser = useReactiveVar(currentUserVar);
    const [persistColumnWidths] = useMutation(setOrderListColumnWidthsTemplate);

    const { data: panNumberFeatureData } = useQuery(panNumberFeatureStatusTemplate);
    const panNumberEnabled: boolean = panNumberFeatureData?.currentLab?.panNumberEnabled ?? false;

    const activeProfile = profile ?? SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE;
    // Single gate for the PAN column: drop it (header + cells) when the lab has the
    // feature disabled, even if a saved profile still references it. The row component
    // relies on this — it always renders the PAN edit action when the cell is present.
    const activeColumns = getActiveOrderListColumnKeys(activeProfile).filter(
        (columnKey) => columnKey !== 'panNumber' || panNumberEnabled,
    );
    const showIconArea = activeProfile.showIconArea ?? true;

    const initialWidths = useMemo<Partial<Record<OrderListColumnKey, number>>>(() => {
        const entry = currentUser?.orderListSettings?.columnWidths?.find(
            (item) => item.profileId === activeProfile._id,
        );
        if (!entry) return {};
        const result: Partial<Record<OrderListColumnKey, number>> = {};
        for (const { columnKey, width } of entry.widths) {
            if (columnKey in ORDER_LIST_COLUMN_DEFINITIONS) {
                result[columnKey as OrderListColumnKey] = width;
            }
        }
        return result;
    }, [currentUser?.orderListSettings?.columnWidths, activeProfile._id]);

    const handlePersistWidths = (widths: Record<string, number>) => {
        if (isSystemDefaultProfile(activeProfile)) return;
        const widthsArray = Object.entries(widths).map(([columnKey, width]) => ({ columnKey, width }));
        const previousSettings = currentUser?.orderListSettings ?? {};
        const previousColumnWidths = previousSettings.columnWidths ?? [];
        const filtered = previousColumnWidths.filter((entry) => entry.profileId !== activeProfile._id);
        const nextColumnWidths = widthsArray.length
            ? [...filtered, { profileId: activeProfile._id, widths: widthsArray }]
            : filtered;
        currentUserVar({
            ...currentUser,
            orderListSettings: { ...previousSettings, columnWidths: nextColumnWidths },
        });
        persistColumnWidths({
            variables: { profileId: activeProfile._id, widths: widthsArray },
        });
    };

    const renderHeaderLabel = (columnKey: OrderListColumnKey) => {
        const definition = ORDER_LIST_COLUMN_DEFINITIONS[columnKey];

        if (columnKey === 'status' && isMobile) return null;

        const labelText = translate(`${definition.i18nNamespace}:${definition.labelKey}`);

        if (definition.sortable && setOrderOnField) {
            return (
                <Tooltip title={labelText} enterDelay={500} placement="top">
                    <TableSortLabel
                        onClick={() => setOrderOnField(definition.sortable!.field)}
                        active={orderBy?.orderBy === definition.sortable.field}
                        direction={orderBy?.orderDirection}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                    >
                        <Box
                            component="span"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            {labelText}
                        </Box>
                    </TableSortLabel>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={labelText} enterDelay={500} placement="top">
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {labelText}
                </Box>
            </Tooltip>
        );
    };

    return (
        <ResizableProfileTable<Order, OrderListColumnKey>
            profileId={activeProfile._id}
            initialWidths={initialWidths}
            onPersistWidths={handlePersistWidths}
            columns={ORDER_LIST_COLUMN_DEFINITIONS}
            activeColumns={activeColumns}
            items={orders}
            getItemKey={(order) => order._id}
            renderHeaderLabel={renderHeaderLabel}
            tableSize="medium"
            minColumnWidth={ORDER_LIST_COLUMN_MIN_WIDTH}
            maxColumnWidth={ORDER_LIST_COLUMN_MAX_WIDTH}
            leadingFrameworkCells={[
                { width: ORDER_LIST_FRAMEWORK_CELL_WIDTHS.flag, renderHeaderCell: () => <TableCell /> },
            ]}
            trailingFrameworkCells={[
                ...(showIconArea
                    ? [{ width: ORDER_LIST_FRAMEWORK_CELL_WIDTHS.iconArea, renderHeaderCell: () => <TableCell /> }]
                    : []),
                { width: ORDER_LIST_FRAMEWORK_CELL_WIDTHS.moreVert, renderHeaderCell: () => <TableCell /> },
            ]}
            renderRow={({ item, activeColumns: rowActiveColumns, index }) => (
                <OrderListRow
                    tableType={tableType}
                    order={item}
                    onOrderContextMenu={onContextMenu}
                    onOrderItemEvent={onOrderItemEvent}
                    batchActionSelected={batchActionSelected}
                    onHandleCheckedOrders={onHandleCheckedOrders}
                    orderIndex={index}
                    activeColumns={rowActiveColumns}
                    showIconArea={showIconArea}
                    isAtLimit={isAtLimit}
                    isChecked={checkedOrderIds?.has(item._id as string) ?? false}
                />
            )}
        />
    );
};

export default OrderListTable;
